import { ApiError } from '@/api/client';

export interface ValidationErrors {
    [field: string]: string;
}

export interface FormattedError {
    message: string;
    validationErrors?: ValidationErrors;
    isValidationError: boolean;
}

/**
 * Extract validation errors from API error response.
 *
 * Handles both backend validation error formats:
 * - ApiExceptionSubscriber: { "error": "Validation failed", "details": { "field": "message" } }
 * - Controllers that validate by hand, such as RegisterController: { "errors": { "field": "message" } }
 */
export function extractValidationErrors(error: unknown): ValidationErrors {
    if (error instanceof ApiError && error.data) {
        const data = error.data as { details?: ValidationErrors; errors?: ValidationErrors };
        const fields = data.details ?? data.errors;
        if (fields && typeof fields === 'object') {
            return fields;
        }
    }
    return {};
}

/**
 * Format an API error for display to users.
 *
 * Returns a user-friendly error message and extracted validation errors.
 */
export function formatApiError(error: unknown): FormattedError {
    if (error instanceof ApiError) {
        const data = error.data as {
            error?: string;
            details?: ValidationErrors;
        };

        const validationErrors = extractValidationErrors(error);
        const hasValidationErrors = Object.keys(validationErrors).length > 0;

        return {
            message: data.error || error.message || 'An error occurred',
            validationErrors: hasValidationErrors ? validationErrors : undefined,
            isValidationError: hasValidationErrors,
        };
    }

    if (error instanceof Error) {
        return {
            message: error.message,
            isValidationError: false,
        };
    }

    return {
        message: 'An unexpected error occurred',
        isValidationError: false,
    };
}

/**
 * Display a toast notification for an error.
 *
 * For validation errors, shows a generic message since field-level errors
 * should be displayed inline in the form.
 *
 * For other errors, shows the specific error message.
 */
export function getErrorMessage(error: unknown): string {
    const formatted = formatApiError(error);

    if (formatted.isValidationError) {
        return 'Please correct the errors in the form';
    }

    return formatted.message;
}

/**
 * Check if an error is a not found error (404).
 */
export function isNotFoundError(error: unknown): boolean {
    return error instanceof ApiError && error.status === 404;
}

/**
 * Check if an error is an unauthorized error (401).
 */
export function isUnauthorizedError(error: unknown): boolean {
    return error instanceof ApiError && error.status === 401;
}

/**
 * Check if an error is a forbidden error (403).
 */
export function isForbiddenError(error: unknown): boolean {
    return error instanceof ApiError && error.status === 403;
}

/**
 * Check if an error is a validation error (422).
 */
export function isValidationError(error: unknown): boolean {
    return error instanceof ApiError && error.status === 422;
}
